import React, { useState } from 'react';
import {
  Button,
  FormField,
  FormSection,
  FormGrid,
  FormActions,
  SectionHeader,
  LoadingBlock,
  DataTableCard,
  DataTableRow,
  DataTableCell,
  Input,
  Select,
  EmptyState,
} from '../../../shared/ui/composites';
import {
  useCreateCommissionRuleMutation,
  useDeleteCommissionRuleMutation,
  useGetCommissionRulesPageData,
} from '../api/react-query';
import { fmtInt } from '../../../shared/utils/formatters';
import { formatDate } from '../../../shared/utils/numberLocale';

const RULES_TABLE_HEAD_CLASS =
  'border-b border-[#e5e7eb] px-3.5 py-3 text-right align-middle bg-[#f8fafc] !text-[#475569] text-sm font-extrabold whitespace-nowrap';

const RULES_COLUMNS = [
  { key: 'staff', label: 'پرسنل', className: RULES_TABLE_HEAD_CLASS },
  { key: 'manufacturer', label: 'تولیدکننده', className: RULES_TABLE_HEAD_CLASS },
  { key: 'sales_target', label: 'هدف فروش', className: RULES_TABLE_HEAD_CLASS },
  { key: 'customers_target', label: 'هدف مشتریان', className: RULES_TABLE_HEAD_CLASS },
  { key: 'debt_balance', label: 'موجودی بدهی', className: RULES_TABLE_HEAD_CLASS },
  { key: 'date_type', label: 'نوع تاریخ', className: RULES_TABLE_HEAD_CLASS },
  { key: 'start_date', label: 'تاریخ شروع', className: RULES_TABLE_HEAD_CLASS },
  { key: 'end_date', label: 'تاریخ پایان', className: RULES_TABLE_HEAD_CLASS },
  { key: 'actions', label: 'عملیات', className: RULES_TABLE_HEAD_CLASS },
];

function RulesTab() {
  const { rules, manufacturers, staff, isLoading } = useGetCommissionRulesPageData();
  const createRule = useCreateCommissionRuleMutation();
  const deleteRule = useDeleteCommissionRuleMutation();

  const [showForm, setShowForm] = useState(false);
  const [searchManufacturer, setSearchManufacturer] = useState('');
  const [formData, setFormData] = useState({
    staff_code: '',
    manufacturer_code: '',
    sales_target: 0,
    customers_target: 0,
    debt_balance: 0,
    date_type: 0,
    dealer_type: '',
    start_date: '',
    end_date: '',
  });

  const rulesList = rules.data || [];
  const manufacturersList = manufacturers.data || [];
  const staffList = staff.data || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createRule.mutateAsync(formData);
      setShowForm(false);
      setFormData({
        staff_code: '',
        manufacturer_code: '',
        sales_target: 0,
        customers_target: 0,
        debt_balance: 0,
        date_type: 0,
        dealer_type: '',
        start_date: '',
        end_date: '',
      });
    } catch (error) {
      console.error('Error saving rule:', error);
      alert('خطا در ذخیره قانون');
    }
  };

  const handleDelete = async (staffCode, manufacturerCode) => {
    if (!window.confirm('آیا از حذف این قانون اطمینان دارید؟')) {
      return;
    }
    try {
      await deleteRule.mutateAsync({ staffCode, manufacturerCode });
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('خطا در حذف قانون');
    }
  };

  const filteredManufacturers = manufacturersList.filter(m =>
    m.ManufacturerName?.toLowerCase().includes(searchManufacturer.toLowerCase()) ||
    m.ManufacturerCode?.toLowerCase().includes(searchManufacturer.toLowerCase())
  );

  return (
    <div className="w-full">
      <SectionHeader
        title="مدیریت قوانین پورسانت"
        action={
          <Button
            variant={showForm ? 'cancel' : 'add'}
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? '❌ لغو' : '➕ افزودن قانون جدید'}
          </Button>
        }
      />

      {showForm && (
        <FormSection>
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormField label="پرسنل" required>
                <Select
                  variant="form"
                  value={formData.staff_code}
                  onChange={(e) => setFormData({ ...formData, staff_code: e.target.value })}
                  required
                >
                  <option value="">انتخاب کنید...</option>
                  {staffList.map(s => (
                    <option key={s.PersCode} value={s.PersCode}>
                      {s.FullName} ({s.PersCode})
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="تولیدکننده" required>
                <div className="flex flex-col gap-2">
                  <Input
                    variant="search"
                    type="text"
                    placeholder="جستجو..."
                    value={searchManufacturer}
                    onChange={(e) => setSearchManufacturer(e.target.value)}
                  />
                  <Select
                    variant="form"
                    size="list"
                    value={formData.manufacturer_code}
                    onChange={(e) => setFormData({ ...formData, manufacturer_code: e.target.value })}
                    required
                  >
                    <option value="">انتخاب کنید...</option>
                    {filteredManufacturers.map(m => (
                      <option key={m.ManufacturerCode} value={m.ManufacturerCode}>
                        {m.ManufacturerName} ({m.ManufacturerCode})
                      </option>
                    ))}
                  </Select>
                </div>
              </FormField>

              <FormField label="هدف فروش">
                <Input
                  variant="form"
                  type="number"
                  value={formData.sales_target}
                  onChange={(e) => setFormData({ ...formData, sales_target: parseInt(e.target.value) || 0 })}
                />
              </FormField>

              <FormField label="هدف مشتریان">
                <Input
                  variant="form"
                  type="number"
                  value={formData.customers_target}
                  onChange={(e) => setFormData({ ...formData, customers_target: parseInt(e.target.value) || 0 })}
                />
              </FormField>

              <FormField label="موجودی بدهی">
                <Input
                  variant="form"
                  type="number"
                  step="0.01"
                  value={formData.debt_balance}
                  onChange={(e) => setFormData({ ...formData, debt_balance: parseFloat(e.target.value) || 0 })}
                />
              </FormField>

              <FormField label="نوع تاریخ">
                <Select
                  variant="form"
                  value={formData.date_type}
                  onChange={(e) => setFormData({ ...formData, date_type: parseInt(e.target.value) })}
                >
                  <option value="0">روزانه</option>
                  <option value="1">هفتگی</option>
                  <option value="2">ماهانه</option>
                  <option value="3">سالانه</option>
                </Select>
              </FormField>

              <FormField label="نوع نمایندگی">
                <Input
                  variant="form"
                  type="text"
                  value={formData.dealer_type}
                  onChange={(e) => setFormData({ ...formData, dealer_type: e.target.value })}
                />
              </FormField>

              <FormField label="تاریخ شروع">
                <Input
                  variant="form"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </FormField>

              <FormField label="تاریخ پایان">
                <Input
                  variant="form"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </FormField>
            </FormGrid>

            <FormActions>
              <Button type="submit" variant="submit">ذخیره</Button>
              <Button type="button" variant="cancel" onClick={() => setShowForm(false)}>
                لغو
              </Button>
            </FormActions>
          </form>
        </FormSection>
      )}

      {isLoading ? (
        <LoadingBlock message="در حال بارگذاری..." />
      ) : rulesList.length === 0 ? (
        <EmptyState message="هیچ قانونی تعریف نشده است" />
      ) : (
        <DataTableCard
          columns={RULES_COLUMNS}
          isEmpty={false}
          minWidth="820px"
        >
          {rulesList.map((rule, index) => (
            <DataTableRow key={index} className="hover:bg-[#f8faff]">
              <DataTableCell>{rule.staff_name || rule.staff_code}</DataTableCell>
              <DataTableCell>{rule.manufacturer_name || rule.manufacturer_code}</DataTableCell>
              <DataTableCell>{fmtInt(rule.sales_target)}</DataTableCell>
              <DataTableCell>{fmtInt(rule.customers_target)}</DataTableCell>
              <DataTableCell>{fmtInt(rule.debt_balance)}</DataTableCell>
              <DataTableCell>
                {rule.date_type === 0 ? 'روزانه' :
                 rule.date_type === 1 ? 'هفتگی' :
                 rule.date_type === 2 ? 'ماهانه' : 'سالانه'}
              </DataTableCell>
              <DataTableCell>{rule.start_date ? formatDate(rule.start_date) : '-'}</DataTableCell>
              <DataTableCell>{rule.end_date ? formatDate(rule.end_date) : '-'}</DataTableCell>
              <DataTableCell>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(rule.staff_code, rule.manufacturer_code)}
                >
                  🗑️
                </Button>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableCard>
      )}
    </div>
  );
}

export default RulesTab;
